import React, {useState, useMemo} from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TouchableWithoutFeedback,
} from 'react-native';
import dayjs from 'dayjs';

import 'dayjs/locale/fr';
import {capitalizeFirstLetter} from '../../../../services/technique/utils';
import styles from './styles';

interface CalendarModalProps {
  modalVisible: boolean;
  onClose: () => void;
  value: Date | undefined;
  onChange: (value: Date) => void;
  defaultValue?: Date;
  min?: Date; // Add min date prop
  max?: Date; // Add max date prop
}

const CalendarModal = ({
  modalVisible,
  onClose,
  value,
  onChange,
  defaultValue,
  min,
  max,
}: CalendarModalProps) => {
  const [currentDate, setCurrentDate] = useState<Date>(
    value || defaultValue || new Date(),
  );

  React.useEffect(() => {
    if (value && value?.toDateString() !== currentDate.toDateString())
      setCurrentDate(value || defaultValue || new Date());
  }, [value, defaultValue]);

  const [type, setType] = React.useState<'calendar' | 'year' | 'month'>(
    'calendar',
  );

  // Memoize today's date for comparison
  const todayDate = useMemo(() => new Date(), []);

  // Calculate current month and year
  const currentMonth: string = useMemo(() => {
    return capitalizeFirstLetter(
      dayjs(currentDate).locale('fr').format('MMMM'),
    );
  }, [currentDate]);

  const currentYear = useMemo(
    () => currentDate.getFullYear() || new Date().getFullYear(),
    [currentDate],
  );

  const minYear = min?.getFullYear() || 1900;
  const maxYear = max?.getFullYear() || 2100;

  const years: Array<{value: string; label: string}> = Array.from(
    {length: maxYear - minYear + 1},
    (_, index) => ({
      value: `${minYear + index}`,
      label: `${minYear + index}`,
    }),
  );

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendar = (month: number, year: number) => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);

    const calendar = [];
    let dayCount = 1;

    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < firstDay) {
          week.push('');
        } else if (dayCount > daysInMonth) {
          week.push('');
        } else {
          week.push(dayCount);
          dayCount++;
        }
      }
      calendar.push(week);
    }
    return calendar;
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentDate(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentDate(nextMonth);
  };

  const handleChooseYear = (value: string) => {
    const updatedDate = new Date(currentDate);
    updatedDate.setFullYear(parseInt(value));
    setCurrentDate(updatedDate);
    setType('month');
  };

  const handleChooseMonth = (value: string) => {
    const updatedDate = new Date(currentDate);
    updatedDate.setMonth(parseInt(value));
    setCurrentDate(updatedDate);
    setType('calendar');
  };

  const months: Array<{value: string; label: string}> = [
    {label: 'Janvier', value: '0'},
    {label: 'Fevrier', value: '1'},
    {label: 'Mars', value: '2'},
    {label: 'Avril', value: '3'},
    {label: 'Mai', value: '4'},
    {label: 'Juin', value: '5'},
    {label: 'Juillet', value: '6'},
    {label: 'Aout', value: '7'},
    {label: 'Septembre', value: '8'},
    {label: 'Octobre', value: '9'},
    {label: 'Novembre', value: '10'},
    {label: 'Decembre', value: '11'},
  ];

  const monthsDataOptions = months.map((element, index) => (
    <TouchableOpacity
      key={index}
      style={styles.calendar.monthDataOptionsContainer}
      onPress={() => handleChooseMonth(element.value)}>
      <Text style={styles.calendar.monthDataOptionsText}>{element.label}</Text>
    </TouchableOpacity>
  ));

  const yearsDataOptions = years.map((element, index) => (
    <TouchableOpacity
      key={index}
      style={styles.calendar.yearDataOptionsContainer}
      onPress={() => handleChooseYear(element.value)}>
      <Text style={styles.calendar.yearDataOptionsText}>{element.label}</Text>
    </TouchableOpacity>
  ));

  const handleClose = () => {
    onClose();
    setCurrentDate(new Date());
    setType('calendar');
  };

  const isDateInRange = (date: Date): boolean => {
    if (min && date < min) return false;
    if (max && date > max) return false;
    return true;
  };

  const RenderCalendar = ({currentDate}: {currentDate: Date}) => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const calendar = generateCalendar(month, year);

    return (
      <>
        <View style={styles.calendar.weekRow}>
          {daysOfWeek.map((day, index) => (
            <Text key={index} style={styles.calendar.weekDay}>
              {day}
            </Text>
          ))}
        </View>
        {calendar.map((week, weekIndex) => (
          <View key={weekIndex} style={styles.calendar.weekRow}>
            {week.map((day, dayIndex) => {
              const isSelectedDay =
                day === value?.getDate() &&
                month === value?.getMonth() &&
                year === value?.getFullYear();
              const isToday =
                day === todayDate.getDate() &&
                month === todayDate.getMonth() &&
                year === todayDate.getFullYear();
              const dayDate = new Date(year, month, Number(day));

              const isOutOfRange = day ? !isDateInRange(dayDate) : false;

              return (
                <TouchableOpacity
                  key={dayIndex}
                  onPress={() => {
                    if (day && !isOutOfRange) {
                      const newDate = new Date(year, month, Number(day));
                      setCurrentDate(newDate);
                      onChange(newDate);
                      handleClose();
                    }
                  }}>
                  <Text
                    style={[
                      styles.calendar.day,
                      isSelectedDay && styles.calendar.selectedDay,
                      isToday && styles.calendar.today,
                      isOutOfRange && styles.calendar.outOfRangeDay, // Style for out-of-range days
                    ]}>
                    {day || ' '}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </>
    );
  };

  return (
    <Modal visible={modalVisible} transparent={true} animationType="slide">
      <TouchableWithoutFeedback onPress={handleClose}>
        <View style={styles.calendar.modalContainer}>
          <TouchableWithoutFeedback>
            {type === 'calendar' ? (
              <View style={styles.calendar.calendarContainer}>
                <TouchableOpacity
                  style={styles.calendar.closeButton}
                  onPress={handleClose}>
                  <Text style={styles.calendar.closeButtonText}>X</Text>
                </TouchableOpacity>
                <View style={styles.calendar.header}>
                  <TouchableOpacity onPress={handlePrevMonth}>
                    <Text style={styles.calendar.navButton}>{'<'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setType('year')}>
                    <Text style={styles.calendar.headerText}>
                      {currentYear.toString()}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setType('month')}>
                    <Text style={styles.calendar.headerText}>
                      {currentMonth}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={handleNextMonth}>
                    <Text style={styles.calendar.navButton}>{'>'}</Text>
                  </TouchableOpacity>
                </View>
                <View>
                  <RenderCalendar currentDate={currentDate} />
                </View>
              </View>
            ) : type === 'year' ? (
              <View style={styles.calendar.calendarContainer}>
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  style={styles.calendar.yearScroll}>
                  {yearsDataOptions}
                </ScrollView>
              </View>
            ) : (
              <View style={styles.calendar.calendarContainer}>
                <View>{monthsDataOptions}</View>
              </View>
            )}
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default CalendarModal;
